/*
  Admin dashboard — lives at /_vh-admin/dashboard. Sections:
    1. Live stats
    2. Ad configuration (AdSense / EthicalAds / Custom HTML per slot)
    3. Analytics IDs (GA4 / GTM)
    4. Site assets (logo upload/reset)
    5. Backend secrets status (read-only)
*/
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Shield, BarChart3, Megaphone, LineChart, Image as ImageIcon,
  KeyRound, CheckCircle2, XCircle, Save, Upload, RotateCcw, Eye, ToggleLeft,
} from "lucide-react";
import { adminApi } from "../lib/adminApi";
import { API } from "../lib/api";
import { writePreview } from "../lib/runtimeConfig";

function Section({ icon: Icon, title, desc, children, testid }) {
  return (
    <section className="card-tactile rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-start gap-3 mb-5">
        <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-[color:var(--primary-ink)]" />
        </div>
        <div>
          <h2 className="font-display font-bold text-xl tracking-tight">{title}</h2>
          {desc && <p className="text-sm mt-0.5" style={{ color: "var(--text-muted)" }}>{desc}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, value, onChange, placeholder, testid, type = "text", readOnly = false }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
      {hint && <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>{hint}</div>}
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2 rounded-md border bg-[color:var(--surface)] text-[color:var(--text-main)] focus:outline-none focus:border-[color:var(--primary)] font-mono text-sm"
        style={{ borderColor: "var(--border-subtle)" }}
      />
    </label>
  );
}

function TextArea({ label, hint, value, onChange, placeholder, testid, rows = 4 }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
      {hint && <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>{hint}</div>}
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2 rounded-md border bg-[color:var(--surface)] text-[color:var(--text-main)] focus:outline-none focus:border-[color:var(--primary)] font-mono text-xs"
        style={{ borderColor: "var(--border-subtle)" }}
      />
    </label>
  );
}

function StatusPill({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok
        ? <CheckCircle2 className="h-4 w-4" style={{ color: "var(--success)" }} />
        : <XCircle className="h-4 w-4" style={{ color: "var(--text-muted)" }} />}
      <span>{label}</span>
      <span className="text-xs" style={{ color: "var(--text-muted)" }}>{ok ? "configured" : "not set"}</span>
    </div>
  );
}

function FlagRow({ k, label, value, onChange }) {
  return (
    <label className="flex items-center gap-3 py-1.5 cursor-pointer select-none">
      <button type="button" onClick={() => onChange(!value)} data-testid={`flag-${k}`}
        aria-pressed={value}
        className="relative h-5 w-9 rounded-full border transition-colors shrink-0"
        style={{
          background: value ? "var(--primary)" : "var(--surface-2)",
          borderColor: value ? "var(--primary)" : "var(--border-subtle)",
        }}>
        <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
          style={{ left: value ? "calc(100% - 18px)" : "2px" }} />
      </button>
      <span className="text-sm">{label}</span>
    </label>
  );
}

export default function AdminDashboardPage() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    Promise.all([
      adminApi.me().catch(() => null),
      adminApi.getConfig().catch(() => null),
      adminApi.stats().catch(() => null),
    ]).then(([me, cfg, st]) => {
      if (!mounted) return;
      if (!me) { nav("/_vh-admin", { replace: true }); return; }
      setConfig(cfg);
      setStats(st);
      // Seed the editable form from the resolved config so it shows both
      // env-only values AND the admin's own overrides side-by-side.
      setForm({
        adsense_client_id:            cfg?.resolved?.adsense_client_id || "",
        adsense_slot_in_feed:         cfg?.resolved?.adsense_slot_in_feed || "",
        adsense_slot_desktop_inline:  cfg?.resolved?.adsense_slot_desktop_inline || "",
        adsense_slot_sidebar:         cfg?.resolved?.adsense_slot_sidebar || "",
        ethicalads_id:                cfg?.resolved?.ethicalads_id || "",
        ga_id:                        cfg?.resolved?.ga_id || "",
        gtm_id:                       cfg?.resolved?.gtm_id || "",
        custom_ad_in_feed:            cfg?.resolved?.custom_ads?.in_feed || "",
        custom_ad_desktop_inline:     cfg?.resolved?.custom_ads?.desktop_inline || "",
        custom_ad_sidebar:            cfg?.resolved?.custom_ads?.sidebar || "",
        feature_flags:                cfg?.resolved?.feature_flags || {},
        dinner_plate_max_menu_files:  cfg?.resolved?.dinner_plate_max_menu_files ?? 3,
      });
    });
    return () => { mounted = false; };
  }, [nav]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function saveAll() {
    setSaving(true);
    try {
      const next = await adminApi.updateConfig(form);
      setConfig(c => ({ ...c, resolved: next, overrides_in_db: { ...(c?.overrides_in_db || {}), ...form } }));
      toast.success("Saved — live for all visitors on next page load");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function logout() {
    try { await adminApi.logout(); } catch { /* noop */ }
    nav("/_vh-admin", { replace: true });
  }

  function previewAsVisitor() {
    const url = writePreview(form);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function pickLogo(file) {
    if (!file) return;
    setLogoBusy(true);
    try {
      await adminApi.uploadLogo(file);
      toast.success("Logo updated");
      // Bust the cached logo by bumping a timestamp
      setConfig(c => c ? { ...c, resolved: { ...c.resolved, logo_override: true, _logo_v: Date.now() } } : c);
    } catch (e) { toast.error(e.message); }
    finally { setLogoBusy(false); }
  }

  async function resetLogo() {
    setLogoBusy(true);
    try {
      await adminApi.resetLogo();
      toast.success("Reverted to default logo");
      setConfig(c => c ? { ...c, resolved: { ...c.resolved, logo_override: false, _logo_v: Date.now() } } : c);
    } catch (e) { toast.error(e.message); }
    finally { setLogoBusy(false); }
  }

  if (!config) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>Loading…</div>;
  }

  const secrets = config?.secrets_status || {};
  const logoV = config?.resolved?._logo_v || "";
  const logoSrc = config?.resolved?.logo_override
    ? `${API}/admin/assets/logo/current${logoV ? `?v=${logoV}` : ""}`
    : "/logo.svg";

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text-main)" }}>
      <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-6 border-b backdrop-blur"
        style={{ background: "color-mix(in srgb, var(--surface) 86%, transparent)", borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center">
            <Shield className="h-5 w-5 text-[color:var(--primary-ink)]" />
          </div>
          <div>
            <div className="font-display font-black tracking-tighter text-lg leading-none">VeriHandi admin</div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Restricted control panel</div>
          </div>
        </div>
        <button onClick={logout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-semibold hover:bg-[color:var(--surface-2)]">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        <Section icon={BarChart3} title="Live stats" testid="admin-stats"
          desc="Lightweight read-only snapshot — refresh to update.">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Active devices", stats?.active_devices ?? "—"],
              ["Prescriptions",  stats?.prescriptions_total ?? "—"],
              ["Doses",          stats?.doses_total ?? "—"],
              ["Digest subs",    stats?.digest_subscribers ?? "—"],
              ["Donations (#)",  stats?.donations?.count ?? 0],
              ["Donations (£)",  ((stats?.donations?.total_pence ?? 0) / 100).toFixed(2)],
            ].map(([k, v]) => (
              <div key={k} className="card-tactile rounded-xl p-3">
                <div className="label-caps text-[10px]">{k}</div>
                <div className="font-display font-black text-xl mt-1">{v}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Ad config */}
        <Section icon={Megaphone} title="Ad networks" testid="admin-ads"
          desc="Priority: Custom HTML → AdSense → EthicalAds → empty placeholder. Leave all blank on a slot to fall back to the next source.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="AdSense Client ID" hint="ca-pub-XXXXXXXXXXXXXXXX"
              value={form.adsense_client_id} onChange={(v) => set("adsense_client_id", v)}
              placeholder="ca-pub-…" testid="cfg-adsense-client" />
            <Field label="EthicalAds Publisher ID" hint="Flip when monthly views grow"
              value={form.ethicalads_id} onChange={(v) => set("ethicalads_id", v)}
              placeholder="your-publisher-id" testid="cfg-ethicalads" />
            <Field label="AdSense slot · in-feed 300×250" value={form.adsense_slot_in_feed}
              onChange={(v) => set("adsense_slot_in_feed", v)} placeholder="1234567890" testid="cfg-adsense-in-feed" />
            <Field label="AdSense slot · desktop-inline 728×90" value={form.adsense_slot_desktop_inline}
              onChange={(v) => set("adsense_slot_desktop_inline", v)} placeholder="1234567890" testid="cfg-adsense-desktop" />
            <Field label="AdSense slot · sidebar 300×600" value={form.adsense_slot_sidebar}
              onChange={(v) => set("adsense_slot_sidebar", v)} placeholder="1234567890" testid="cfg-adsense-sidebar" />
          </div>
          <div className="mt-6 space-y-3">
            <div className="label-caps">Custom ads (HTML)</div>
            <p className="text-xs -mt-1" style={{ color: "var(--text-muted)" }}>
              Paste any HTML — direct sponsor creative, affiliate banners, newsletter CTAs. Rendered in a sandboxed iframe. Overrides the corresponding network slot.
            </p>
            <TextArea label="In-feed 300×250" value={form.custom_ad_in_feed} onChange={(v) => set("custom_ad_in_feed", v)}
              placeholder='<a href="https://…"><img src="https://…/banner-300x250.png" width="300" height="250"/></a>' testid="cfg-custom-in-feed" />
            <TextArea label="Desktop inline 728×90" value={form.custom_ad_desktop_inline}
              onChange={(v) => set("custom_ad_desktop_inline", v)} placeholder='<!-- 728×90 HTML -->' testid="cfg-custom-desktop" rows={3} />
            <TextArea label="Sidebar 300×600" value={form.custom_ad_sidebar}
              onChange={(v) => set("custom_ad_sidebar", v)} placeholder='<!-- 300×600 HTML -->' testid="cfg-custom-sidebar" rows={5} />
          </div>
        </Section>

        {/* Analytics */}
        <Section icon={LineChart} title="Analytics" testid="admin-analytics"
          desc="GA4 Measurement ID and/or GTM container ID. Events only flow after a visitor accepts the consent banner.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="GA4 Measurement ID" hint="G-XXXXXXXXXX" value={form.ga_id}
              onChange={(v) => set("ga_id", v)} placeholder="G-…" testid="cfg-ga" />
            <Field label="GTM Container ID" hint="GTM-XXXXXXX" value={form.gtm_id}
              onChange={(v) => set("gtm_id", v)} placeholder="GTM-…" testid="cfg-gtm" />
          </div>
        </Section>

        {/* Site logo */}
        <Section icon={ImageIcon} title="Site logo" testid="admin-logo"
          desc="Overrides /logo.svg site-wide. SVG, PNG, WEBP or JPEG — max 512 KB.">
          <div className="flex items-center gap-5">
            <img src={logoSrc} alt="Current logo" className="h-16 w-16 rounded-xl border p-2"
              style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}
              data-testid="admin-logo-preview" />
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/svg+xml,image/png,image/webp,image/jpeg"
                className="hidden" data-testid="admin-logo-file"
                onChange={(e) => pickLogo(e.target.files?.[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={logoBusy}
                data-testid="admin-logo-upload"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-lg font-semibold text-sm text-white disabled:opacity-50"
                style={{ background: "var(--primary)" }}>
                <Upload className="h-4 w-4" /> {logoBusy ? "Uploading…" : "Upload new"}
              </button>
              {config?.resolved?.logo_override && (
                <button onClick={resetLogo} disabled={logoBusy} data-testid="admin-logo-reset"
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-lg font-semibold text-sm border hover:bg-[color:var(--surface-2)]"
                  style={{ borderColor: "var(--border-subtle)" }}>
                  <RotateCcw className="h-4 w-4" /> Revert
                </button>
              )}
            </div>
          </div>
        </Section>

        {/* Secrets status */}
        <Section icon={KeyRound} title="Backend secrets" testid="admin-secrets"
          desc="These live in backend/.env only and cannot be edited from the admin panel — shown here as a configured/not-set status check.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <StatusPill ok={secrets.gemini_emergent_llm_key} label="Emergent LLM key (Gemini AI parsing)" />
            <StatusPill ok={secrets.stripe_api_key} label="Stripe API key (donations)" />
            <StatusPill ok={secrets.resend_api_key} label="Resend API key (email digests)" />
            <StatusPill ok={secrets.google_oauth_client_id} label="Google OAuth (Calendar push)" />
          </div>
        </Section>

        {/* Feature flags */}
        <Section icon={ToggleLeft} title="Feature flags" testid="admin-flags"
          desc="Turn tools and new features on/off without a redeploy. Takes effect on the next page load for all visitors.">
          <div className="space-y-5">
            <div>
              <div className="label-caps mb-2">Core tools · show on Hub & sidebar</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {[
                  ["show_prescriptions", "Prescriptions (flagship)"],
                  ["show_todo", "To-Do List"],
                  ["show_shopping", "Shopping List"],
                  ["show_notes", "Notes"],
                  ["show_calendar_generator", "Calendar Generator"],
                  ["show_unit_converter", "Unit Converter"],
                  ["show_image_editor", "Image Editor"],
                  ["show_qr_generator", "QR Generator"],
                ].map(([k, label]) => (
                  <FlagRow key={k} k={k} label={label}
                    value={!!(form.feature_flags || {})[k]}
                    onChange={(v) => set("feature_flags", { ...(form.feature_flags || {}), [k]: v })} />
                ))}
              </div>
            </div>
            <div>
              <div className="label-caps mb-2">Roadmap features · enable when ready to ship</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {[
                  ["feature_life_inbox",      "Life Inbox (AI capture) · Phase 1"],
                  ["feature_routines",        "Smart Routines · Phase 2"],
                  ["feature_voice_capture",   "Voice input · Phase 2"],
                  ["feature_auto_scheduler",  "AI Auto-Scheduler · Phase 2"],
                  ["feature_subscriptions",   "Life Admin (subs/bills) · Phase 3"],
                  ["feature_text_to_checklist", "Text → Checklist · Phase 3"],
                  ["feature_url_summary",     "URL → Summary · Phase 3"],
                  ["feature_ocr_tool",        "Image → Text (OCR) · Phase 3"],
                  ["feature_frequently_bought", "Frequently-bought suggestions · Phase 3"],
                  ["feature_contextual_memory", "Contextual Memory (search all data) · Phase 4"],
                  ["feature_ai_insights",       "AI Insights (daily nudges) · Phase 4"],
                  ["feature_accounts",          "Accounts & multi-device sync · Phase 5a"],
                  ["feature_dinner_plate",      "Dinner Plate (group menu pre-order) · MVP"],
                ].map(([k, label]) => (
                  <FlagRow key={k} k={k} label={label}
                    value={!!(form.feature_flags || {})[k]}
                    onChange={(v) => set("feature_flags", { ...(form.feature_flags || {}), [k]: v })} />
                ))}
              </div>
            </div>

            {/* Feature-specific tunables */}
            <div>
              <div className="label-caps mb-2">Feature limits</div>
              <div className="flex items-start gap-4 flex-wrap" data-testid="admin-tunables">
                <label className="block" data-testid="admin-dp-max-files-row">
                  <span className="text-sm font-medium block">Dinner Plate · Max menu files per event</span>
                  <span className="text-xs block mb-1.5" style={{ color: "var(--text-muted)" }}>
                    How many photos / PDFs a host can upload in one go. Range 1–10.
                  </span>
                  <input
                    type="number" min={1} max={10} step={1}
                    value={form.dinner_plate_max_menu_files ?? 3}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value, 10);
                      const clamped = Number.isFinite(raw) ? Math.max(1, Math.min(10, raw)) : 3;
                      set("dinner_plate_max_menu_files", clamped);
                    }}
                    data-testid="admin-dp-max-files-input"
                    className="w-24 px-3 h-10 rounded-lg border bg-transparent text-sm"
                    style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
                </label>
              </div>
            </div>
          </div>
        </Section>

        {/* Sticky save bar */}
        <div className="sticky bottom-4 z-10 flex justify-end gap-2">
          <button onClick={previewAsVisitor} data-testid="admin-preview"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-xl font-semibold text-sm border shadow-lg hover:bg-[color:var(--surface-2)]"
            style={{ background: "var(--surface)", borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
            <Eye className="h-4 w-4" /> Preview as visitor
          </button>
          <button onClick={saveAll} disabled={saving} data-testid="admin-save"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-xl font-semibold text-sm text-white shadow-lg disabled:opacity-50"
            style={{ background: "var(--primary)" }}>
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save all changes"}
          </button>
        </div>
      </main>
    </div>
  );
}
